import React, { type ComponentPropsWithoutRef, useState } from 'react';

import Popup from '@atlaskit/popup';
import { type SearchConfluencePartial } from '@atlassian/search-client/confluence';
import { useCopyToClipboard } from '@atlassian/search-ui/actions';
import { PersonCard } from '@atlassian/search-ui/card';
import { ShortInlineSocialSignal } from '@atlassian/search-ui/trigger';

import { ACTION_SUBJECT_PREFIX } from '../../constants/analytics';
import type { Nullable } from '../../types';
import { onSocialSignalClickedAnalyticsContext } from '../../utils/events/social-signal-clicked';
import { type CommonAttributes } from '../../utils/events/types';
import { getExtensionData } from '../../utils/relay/get-extension-data';
import { useAppContext } from '../app-context';

interface BreadcrumbSocialSignalProps {
	partial: Pick<SearchConfluencePartial, 'socialSignal'>;
	commonAttributes: CommonAttributes;
}

export const BreadcrumbSocialSignal = ({
	partial,
	commonAttributes,
}: BreadcrumbSocialSignalProps): React.JSX.Element | null => {
	const copyEmailToClipboard = useCopyToClipboard(partial.socialSignal?.user?.email ?? '');
	const [isOpen, setIsOpen] = useState(false);
	const { mapUserIdToProfileUrl } = useAppContext();

	const { socialSignal } = partial;
	if (
		!socialSignal ||
		!socialSignal.user ||
		!socialSignal.signal ||
		!isSupportedSocialSignal(socialSignal.signal)
	) {
		return null;
	}
	const { signal, timestamp, user } = socialSignal;

	const userProfileUrl = mapUserIdToProfileUrl(user.accountId);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			trigger={(triggerProps) => {
				const extensionData = getExtensionData(commonAttributes.isCQL);
				return (
					<ShortInlineSocialSignal
						{...triggerProps}
						userName={user.name}
						socialSignalType={signal}
						avatarUrl={user.picture}
						onClick={() => {
							setIsOpen((open) => !open);
						}}
						testId="search-page-social-signal"
						componentName={`${ACTION_SUBJECT_PREFIX}SocialSignal`}
						analyticsContext={onSocialSignalClickedAnalyticsContext({
							commonAttributes: {
								...commonAttributes,
								// traceId is only available on extensionData when using Relay
								...(extensionData.traceId ? extensionData : {}),
							},
							socialSignal: signal,
						})}
					/>
				);
			}}
			content={() => (
				<PersonCard
					user={{
						name: user.name,
						profilePicUrl: user.picture,
						jobTitle: user.extendedProfile?.jobTitle,
						profileUrl: userProfileUrl,
					}}
					onCopyEmail={copyEmailToClipboard}
					signalDate={timestamp ? new Date(timestamp) : undefined}
					signalType={signal}
				/>
			)}
		/>
	);
};

type SupportedSocialSignal = ComponentPropsWithoutRef<
	typeof ShortInlineSocialSignal
>['socialSignalType'];
const supportedSignals: SupportedSocialSignal[] = ['edit', 'share', 'comment'];
function isSupportedSocialSignal(signalFromApi: any): signalFromApi is SupportedSocialSignal {
	return supportedSignals.includes(signalFromApi);
}

interface BreadcrumbSocialSignalNextProps {
	userAccountId?: string;
	userName: string;
	userEmail: string;
	userPicture: string;
	userJobTitle?: Nullable<string>;
	signal?: Nullable<string>;
	timestamp?: Nullable<string>;
	commonAttributes: CommonAttributes;
}
export const BreadcrumbSocialSignalNext = ({
	userAccountId,
	userName,
	userEmail,
	userPicture,
	userJobTitle,
	signal,
	timestamp,
	commonAttributes,
}: BreadcrumbSocialSignalNextProps): React.JSX.Element | null => {
	const copyEmailToClipboard = useCopyToClipboard(userEmail);
	const [isOpen, setIsOpen] = useState(false);
	const { mapUserIdToProfileUrl } = useAppContext();
	if (!userAccountId || !userName || !userPicture || !signal || !isSupportedSocialSignal(signal)) {
		return null;
	}
	const userProfileUrl = mapUserIdToProfileUrl(userAccountId);
	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			trigger={(triggerProps) => {
				const extensionData = getExtensionData(commonAttributes.isCQL);
				return (
					<ShortInlineSocialSignal
						{...triggerProps}
						userName={userName}
						socialSignalType={signal}
						avatarUrl={userPicture}
						onClick={() => {
							setIsOpen((open) => !open);
						}}
						testId="search-page-social-signal"
						componentName={`${ACTION_SUBJECT_PREFIX}SocialSignal`}
						analyticsContext={onSocialSignalClickedAnalyticsContext({
							commonAttributes: {
								...commonAttributes,
								// traceId is only available on extensionData when using Relay
								...(extensionData.traceId ? extensionData : {}),
							},
							socialSignal: signal,
						})}
					/>
				);
			}}
			content={() => (
				<PersonCard
					user={{
						name: userName,
						profilePicUrl: userPicture,
						jobTitle: userJobTitle,
						profileUrl: userProfileUrl,
					}}
					onCopyEmail={copyEmailToClipboard}
					signalDate={timestamp ? new Date(timestamp) : undefined}
					signalType={signal}
				/>
			)}
		/>
	);
};
